import type { ComponentProps } from "react";
import { useMemo } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import type { ColumnsType } from "@similarweb/pro-logic-components";

import { useUnmount } from "app/custom-hooks/useUnmount";
import { ErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import { SearchResultsTable, TableFallback } from "app/pages/sales-intelligence/common-components/search-results-table";
import {
    StyledExpandedRowContent,
} from "app/pages/sales-intelligence/common-components/search-results-table/components/styles";
import { useMeasuredHeight } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useMeasuredHeight";
import { useOverwriteWheelEvent } from "app/pages/sales-intelligence/hooks/useOverwriteWheelEvent";
import { LEAD_DOMAINS_TABLE_PAGE_SIZE } from "app/pages/sales-intelligence/modules/leads-search/constants/dto";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import type { NonEmptyString } from "app/pages/sales-intelligence/types/primitives";
import type { RootState } from "single-spa/store/types";

import DomainsTableHeader from "../../../components/DomainsTableHeader/DomainsTableHeader";
import useFetchDomainsTableResults from "../../../hooks/useFetchDomainsTableResults";
import { setExpandedRowPage, setExpandedRowSort, toggleExpandedRow } from "../../../store/action-creators";
import { selectExpandedRowKeys, selectExpandedRowState, selectExpandedRowVariant } from "../../../store/selectors";
import type {
    CompanyInfographicsDto,
    LeadDomainsSearchTableData,
    LeadDomainsTableCompanyModel,
    LeadsSearchColumnListConfig,
    LeadsSearchRow,
    LeadsSearchRowWithKey,
} from "../../../types";

const TABLE_LOADING_ROW_COUNT = LEAD_DOMAINS_TABLE_PAGE_SIZE + 2;

const DEFAULT_TABLE_DATA: LeadDomainsSearchTableData<LeadsSearchRow> = {
    rows: [],
    columns: [],
    totalCount: 0,
    company: {} as CompanyInfographicsDto,
};

type TableProps<R extends object> = ComponentProps<typeof SearchResultsTable<R>>;

type Props<R> = {
    record: R;
    domain: NonEmptyString;
    columnsConfig: LeadsSearchColumnListConfig;
};

const DomainsSearchResultsTableContainerBase = <R extends LeadsSearchRowWithKey>({
     record,
     domain,
     columnsConfig,
}: Props<R>) => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();

    const { pageSize, page } = useSelector(selectExpandedRowState);

    const expandedRowRef = useOverwriteWheelEvent();

    const [contentRef, contentHeight] = useMeasuredHeight<HTMLDivElement>();

    const domainsCompanyModel = useMemo((): LeadDomainsTableCompanyModel => {
        return {
            companyDomain: domain,
            favicon: record?.favicon ?? "",
            companyName: record?.company ?? domain,
            companyOverviewLink: siNavigator.getCompanyAnalysisOverviewLink({
                key: domain,
                areTopLevelDomainsIncluded: true,
            }),
        };
    }, [record, domain]);

    const { data = DEFAULT_TABLE_DATA, isFetching } = useFetchDomainsTableResults(domainsCompanyModel, columnsConfig);

    const paginationProps: TableProps<R>["pagination"] = useMemo(
        () => ({
            pageSize,
            total: data.totalCount,
            current: page,
            onChange(_currentPage: number, _pageSize: number, nextPage) {
                dispatch(setExpandedRowPage(nextPage));
            },
        }),
        [pageSize, page, data.totalCount],
    );

    const handleSortChange: TableProps<R>["onSort"] = ({ order, columnKey }): void => {
        dispatch(setExpandedRowSort({ columnKey: columnKey as string, order }));
    };

    const handleRowCollapse = () => {
        dispatch(toggleExpandedRow({ key: record.key, variant: { kind: "domains" } }));
    };

    useUnmount(() => {
        const state = store.getState();
        const isStillThisDomainsRow =
            selectExpandedRowKeys(state).includes(record.key) && selectExpandedRowVariant(state)?.kind === "domains";

        if (isStillThisDomainsRow) {
            handleRowCollapse();
        }
    });

    return (
        <StyledExpandedRowContent
            data-automation="company-domains-table"
            ref={expandedRowRef}
            $minHeight={contentHeight}
        >
            <div ref={contentRef}>
                <DomainsTableHeader
                    isLoading={isFetching}
                    domainsCompanyModel={domainsCompanyModel}
                    companyData={data.company}
                    onHide={handleRowCollapse}
                />

                <SearchResultsTable<R>
                    isLoading={isFetching}
                    data={data.rows as R[]}
                    columnConfig={data.columns as ColumnsType<R>}
                    pagination={paginationProps}
                    loaderRowCount={TABLE_LOADING_ROW_COUNT}
                    onSort={handleSortChange}
                />
            </div>
        </StyledExpandedRowContent>
    );
};

const TableContainerWithBoundary = <R extends LeadsSearchRowWithKey>(props: Props<R>) => {
    return (
        <ErrorBoundaryWithLogging fallback={<TableFallback isLoading={false} />}>
            <DomainsSearchResultsTableContainerBase {...props} />
        </ErrorBoundaryWithLogging>
    );
};

export default TableContainerWithBoundary;
